import React, { useCallback, useEffect, useReducer, useState } from 'react';

import * as yup from 'yup';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { AdminAxios as Axios } from '../../Assets/Axios/Axios';

import { useToastContext } from '../../Context/ToastContext';

import { CompanyInformationQuery } from '../../Server/Query';
import { UpdateCompanyInformation } from '../../Server/Mutation';

import { uploadFileAdmin as uploadFile, UPLOAD_FOLDERS } from '../../Services/S3';

function CompanyInformation() {
  const { showToastMessage } = useToastContext();

  const [inputs, updateInputs] = useReducer(
    (state, field) => ({ ...state, ...field }),
    undefined,
    generateInitialInputs
  );

  const [error, updateErrors] = useReducer(
    (state, field) => ({ ...state, ...field }),
    undefined,
    generateInitialErrors
  );

  // Newly-picked logo/banner files (uploaded to S3 on save).
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);

  const fetchCompanyInformation = useCallback(async () => {
    const response = await Axios(CompanyInformationQuery);
    if (response.errors) {
      showToastMessage('Something went wrong, Failed to get company details');
      return undefined;
    }
    updateInputs(parseCompanyInformation(response.data.companyInformation));
  }, [showToastMessage]);

  useEffect(() => {
    fetchCompanyInformation();
  }, [fetchCompanyInformation]);

  function handleChange(event) {
    const { name, value } = event.target;
    updateInputs({ [name]: value });
  }

  async function handleSubmit(event) {
    try {
      event.preventDefault();
      updateErrors(generateInitialErrors());

      const validatedInputs = await schema.validate(inputs, {
        abortEarly: false,
      });

      // Upload freshly-picked logo/banner to S3; keep the existing URL otherwise.
      const logo = logoFile
        ? await uploadFile(logoFile, UPLOAD_FOLDERS.COMPANY_LOGO)
        : inputs.logo ?? null;
      const bannerImage = bannerFile
        ? await uploadFile(bannerFile, UPLOAD_FOLDERS.COMPANY_BANNER)
        : inputs.bannerImage ?? null;

      const response = await Axios(
        UpdateCompanyInformation({ ...validatedInputs, logo, bannerImage })
      );

      if (response.errors) {
        showToastMessage('Something went wrong, Failed to update');
        return undefined;
      }

      setLogoFile(null);
      setBannerFile(null);
      fetchCompanyInformation();
      showToastMessage('Updated successfully', 'success');
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const errors = error.inner.reduce(
          (errors, error) => ({ ...errors, [error.path]: error.message }),
          {}
        );
        updateErrors(errors);
        return undefined;
      }
    }
  }

  return (
    <Container maxWidth='sm'>
      <form onSubmit={handleSubmit} style={{ paddingInline: '1em', paddingBlock: '2em' }}>
        <Typography variant='h5' variantMapping={{ h6: 'h1' }} textAlign='center' mb={3}>
          Company Information
        </Typography>
        <Stack
          gap={2}
          sx={{
            '.MuiTextField-root:not(last-child)': { mt: 1 },
          }}>
          <Typography htmlFor='email' component='label'>
            Email
            <TextField
              fullWidth
              id='email'
              name='email'
              value={inputs.email}
              error={Boolean(error.email)}
              helperText={error.email}
              onChange={handleChange}
              type='text'
              inputMode='email'
            />
          </Typography>
          <Typography component='label' htmlFor='mobileNumber'>
            Mobile Number
            <TextField
              fullWidth
              id='mobileNumber'
              name='mobileNumber'
              value={inputs.mobileNumber}
              error={Boolean(error.mobileNumber)}
              helperText={error.mobileNumber}
              onChange={handleChange}
              type='tel'
              inputMode='tel'
              autoComplete='tel'
            />
          </Typography>
          <Typography component='label' htmlFor='facebookUrl'>
            Facebook URL
            <TextField
              fullWidth
              id='facebookUrl'
              name='facebookUrl'
              value={inputs.facebookUrl}
              error={Boolean(error.facebookUrl)}
              helperText={error.facebookUrl}
              onChange={handleChange}
              type='text'
              inputMode='url'
              autoComplete='url'
            />
          </Typography>
          <Typography component='label' htmlFor='twitterUrl'>
            Twitter URL
            <TextField
              fullWidth
              id='twitterUrl'
              name='twitterUrl'
              value={inputs.twitterUrl}
              error={Boolean(error.twitterUrl)}
              helperText={error.twitterUrl}
              onChange={handleChange}
              type='text'
              inputMode='url'
              autoComplete='url'
            />
          </Typography>
          <Typography htmlFor='whatsappUrl' component='label'>
            Whatsapp URL
            <TextField
              fullWidth
              id='whatsappUrl'
              name='whatsappUrl'
              value={inputs.whatsappUrl}
              error={Boolean(error.whatsappUrl)}
              helperText={error.whatsappUrl}
              onChange={handleChange}
              type='text'
              inputMode='url'
              autoComplete='url'
            />
          </Typography>
          <Typography htmlFor='telegramUrl' component='label'>
            Telegram URL
            <TextField
              fullWidth
              id='telegramUrl'
              name='telegramUrl'
              value={inputs.telegramUrl}
              error={Boolean(error.telegramUrl)}
              helperText={error.telegramUrl}
              onChange={handleChange}
              type='text'
              inputMode='url'
              autoComplete='url'
            />
          </Typography>
          <Typography htmlFor='instagramUrl' component='label'>
            Instagram URL
            <TextField
              fullWidth
              id='instagramUrl'
              name='instagramUrl'
              value={inputs.instagramUrl}
              error={Boolean(error.instagramUrl)}
              helperText={error.instagramUrl}
              onChange={handleChange}
              type='text'
              inputMode='url'
              autoComplete='url'
            />
          </Typography>
          <Box>
            <Typography component='label'>Logo</Typography>
            <Stack direction='row' alignItems='center' gap={2} mt={0.5}>
              {(logoFile || inputs.logo) && (
                <Box
                  component='img'
                  alt='Company logo'
                  src={logoFile ? URL.createObjectURL(logoFile) : inputs.logo}
                  sx={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 1 }}
                />
              )}
              <Button variant='outlined' component='label' size='small'>
                {logoFile || inputs.logo ? 'Change logo' : 'Upload logo'}
                <input
                  hidden
                  type='file'
                  accept='image/*'
                  onChange={e => setLogoFile(e.target.files?.[0] ?? null)}
                />
              </Button>
            </Stack>
          </Box>

          <Box>
            <Typography component='label'>Banner image</Typography>
            <Stack direction='row' alignItems='center' gap={2} mt={0.5}>
              {(bannerFile || inputs.bannerImage) && (
                <Box
                  component='img'
                  alt='Company banner'
                  src={bannerFile ? URL.createObjectURL(bannerFile) : inputs.bannerImage}
                  sx={{ width: 120, height: 64, objectFit: 'cover', borderRadius: 1 }}
                />
              )}
              <Button variant='outlined' component='label' size='small'>
                {bannerFile || inputs.bannerImage ? 'Change banner' : 'Upload banner'}
                <input
                  hidden
                  type='file'
                  accept='image/*'
                  onChange={e => setBannerFile(e.target.files?.[0] ?? null)}
                />
              </Button>
            </Stack>
          </Box>
        </Stack>
        <Button type='submit' color='blue' sx={{ px: 8, mx: 'auto', display: 'table', mt: 3 }}>
          Save
        </Button>
      </form>
    </Container>
  );
}

const fields = [
  'mobileNumber',
  'facebookUrl',
  'twitterUrl',
  'whatsappUrl',
  'telegramUrl',
  'instagramUrl',
  'email',
];

function generateInitialInputs() {
  return fields.reduce((fields, field) => ({ ...fields, [field]: '' }), {});
}

function generateInitialErrors() {
  return fields.reduce((fields, field) => ({ ...fields, [field]: null }), {});
}

function parseCompanyInformation(companyInformation) {
  // socialMedia stored as [facebookUrl, instagramUrl, twitterUrl, telegramUrl, whatsappUrl]
  const [facebookUrl = '', instagramUrl = '', twitterUrl = '', telegramUrl = '', whatsappUrl = ''] =
    companyInformation?.socialMedia ?? [];
  return {
    mobileNumber: companyInformation?.phoneNumber ?? '',
    facebookUrl: facebookUrl ?? '',
    twitterUrl: twitterUrl ?? '',
    whatsappUrl: whatsappUrl ?? '',
    telegramUrl: telegramUrl ?? '',
    instagramUrl: instagramUrl ?? '',
    email: companyInformation?.email ?? '',
    logo: companyInformation?.logo ?? null,
    bannerImage: companyInformation?.bannerImage ?? null,
  };
}

const schema = yup.object({
  email: yup.string().trim().email().label('Email'),
  mobileNumber: yup
    .string()
    .trim()
    .matches(/^((\+94)|(0094)|(0))?\d{9}$/, { message: 'Mobile Number is not valid' })
    .transform(value => `+94${value.slice(-9)}`)
    .label('Mobile Number'),
  facebookUrl: yup
    .string()
    .trim()
    .url()
    .transform(value => value || null)
    .nullable()
    .label('Facebook URL'),
  twitterUrl: yup
    .string()
    .trim()
    .url()
    .transform(value => value || null)
    .nullable()
    .label('Twitter URL'),
  whatsappUrl: yup
    .string()
    .trim()
    .url()
    .transform(value => value || null)
    .nullable()
    .label('Whatsapp URL'),
  telegramUrl: yup
    .string()
    .trim()
    .url()
    .transform(value => value || null)
    .nullable()
    .label('Telegram URL'),
  instagramUrl: yup
    .string()
    .trim()
    .url()
    .transform(value => value || null)
    .nullable()
    .label('Instagram URL'),
});

export default CompanyInformation;
